import { Controller, Path, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useCallback, useState } from 'react';
import { ColorRing } from 'react-loader-spinner';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAddEncounterOnMapModalStore } from './AddEncounterOnMapModalStore';
import Stepper from './Stepper';

import { MLButton } from 'components/Button/Button';
import { MLInput } from 'components/Input/Input';
import {
  encounterCreateSchema,
  EncounterCreateFormValues,
  encounterDraftSchema,
  EncounterDraftFormValues,
} from 'api/InteractiveMap/types';
import { postEncounterOnMap } from 'api/InteractiveMap/postEncounterOnMap';
import { useGetDataToCreateEnc } from 'hooks/InteractiveMap/useGetDataToCreateEnc';
import { MLSelect } from 'components/Select/Select';
import MLTextArea from 'components/Input/TextArea';
import useGetAvailableTechnicians from 'hooks/Object/useGetAvailableTechnicians';

interface AddEncounterOnMapModalProps {
  refetchEncounters: () => void;
}

// Helper to format date for datetime-local input
const getNowForDatetimeLocal = () => {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const AddEncounterOnMapModal: React.FC<AddEncounterOnMapModalProps> = ({ refetchEncounters }) => {
  const { isOpen, onManageAddEncounterOnMapClose, objectSlug, planSlug } = useAddEncounterOnMapModalStore();
  const { data: dataToCreateEnc, isLoading: isLoadingDataToCreateEnc } = useGetDataToCreateEnc(objectSlug, {
    enabled: isOpen,
  });
  const { data: availableTechnicians } = useGetAvailableTechnicians();
  const { t } = useTranslation();

  // Multisteps states
  const [step, setStep] = useState<number>(1);
  const [encType, setEncType] = useState<'pest' | 'traces'>('pest');

  const pestOptions = (dataToCreateEnc?.pest_list ?? []).map(pest => ({
    value: pest.id,
    label: pest.name,
  }));

  const tracesOptions = (dataToCreateEnc?.pest_traces_list ?? []).map(traces => ({
    value: traces.id,
    label: traces.name,
  }));

  const witnessOptions = (dataToCreateEnc?.client_employees ?? []).map(witness => ({
    value: witness.id,
    label: witness.first_name + ' ' + witness.last_name,
  }));

  if (dataToCreateEnc?.request_user_short_info) {
    witnessOptions.push({
      value: dataToCreateEnc.request_user_short_info.id,
      label: dataToCreateEnc.request_user_short_info.name,
    });
  }

  const responsibleOptions = (availableTechnicians ?? []).map(responsePerson => ({
    value: responsePerson.id,
    label: responsePerson.label || responsePerson.email,
  }));

  if (dataToCreateEnc?.object_responsible_person) {
    responsibleOptions.push({
      value: dataToCreateEnc.object_responsible_person.id,
      label: dataToCreateEnc.object_responsible_person.name,
    });
  }

  const { control, handleSubmit, reset, trigger, setValue, clearErrors } = useForm<EncounterCreateFormValues>({
    resolver: zodResolver(step === 1 ? encounterDraftSchema : encounterCreateSchema),
    defaultValues: {
      floor_plan: planSlug || null,
      x_coord: planSlug ? 0 : null,
      y_coord: planSlug ? 0 : null,
      place: '',
      pest: null,
      amount: null,
      pest_traces: null,
      witness: 0,
      discovery_datetime: getNowForDatetimeLocal(),
      responsible_person: null,
      creation_comment: '',
      is_blitz_creation: true,
      photos: [],
    },
  });

  useEffect(() => {
    const getDefaultValues = () => ({
      floor_plan: planSlug || null,
      x_coord: null,
      y_coord: null,
      place: '',
      pest: null,
      amount: null,
      pest_traces: null,
      witness: 0,
      discovery_datetime: getNowForDatetimeLocal(),
      responsible_person: null,
      creation_comment: '',
      is_blitz_creation: true,
      photos: [],
    });

    if (dataToCreateEnc) {
      reset({
        ...getDefaultValues(),
        witness: dataToCreateEnc.request_user_short_info?.id ?? 0,
        responsible_person: dataToCreateEnc.object_responsible_person?.id ?? null,
      });
    }
  }, [dataToCreateEnc, reset, planSlug]);

  const onReset = useCallback(() => {
    reset();
    onManageAddEncounterOnMapClose();
  }, [reset, onManageAddEncounterOnMapClose]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onReset();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onReset]);

  useEffect(() => {
    if (planSlug) {
      setValue('floor_plan', planSlug);
    }
  }, [planSlug, setValue]);

  const handleNextStep = async () => {
    let valid = true;
    if (step === 1) {
      const fields: Extract<keyof EncounterDraftFormValues, string>[] = ['place'];
      if (!planSlug) {
        fields.push('floor_plan');
      }
      if (encType === 'pest') {
        fields.push('pest', 'amount');
      }
      valid = await trigger(fields);
    } else if (step === 2) {
      const fields: Path<EncounterCreateFormValues>[] = [
        'witness',
        'responsible_person',
        'discovery_datetime',
        'creation_comment',
      ];
      valid = await trigger(fields);
    }
    if (valid) {
      setStep(step + 1);
    }
  };

  if (!isOpen) return null;

  const steps = [
    t('MapModals.AddEnc.stepper.quickAdd'),
    t('MapModals.AddEnc.stepper.details'),
    t('MapModals.AddEnc.stepper.confirm'),
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '20px',
        }}
        onClick={onReset}
      >
        {/* Modal Content */}
        <div
          style={{
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: '800px',
            width: 344,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h5 style={{ margin: 0, fontWeight: 600, color: '#333' }}>{t('MapModals.AddEnc.title')}</h5>
            <button
              onClick={onReset}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>

          {/* Stepper */}
          <div className="mx-4">
            <Stepper steps={steps} currentStep={step} />
          </div>

          {/* Body */}
          <div style={{ padding: '8px 24px 24px 24px', flex: 1, overflowY: 'auto' }}>
            {isLoadingDataToCreateEnc ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <ColorRing
                  visible
                  height="80"
                  width="80"
                  ariaLabel="color-ring-loading"
                  wrapperStyle={{}}
                  wrapperClass="color-ring-wrapper"
                  colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
                />
              </div>
            ) : (
              <>
                {step === 1 && (
                  <div>
                    <div className="row mb-2">
                      <div className="col-xl-12">
                        <div className="d-flex justify-content-center">
                          <div className="p-2 bg-light" style={{ width: 240 }}>
                            <div className="d-flex gap-2">
                              <button
                                onClick={() => {
                                  setEncType('pest');
                                  setValue('pest_traces', null);
                                  clearErrors();
                                }}
                                style={{ width: 120 }}
                                className={`btn fs-12 btn${encType !== 'pest' ? '-ghost' : ''}-purple`}
                              >
                                {t('MapModals.AddEnc.step1.pest')}
                              </button>
                              <button
                                onClick={() => {
                                  setEncType('traces');
                                  setValue('pest', null);
                                  setValue('amount', null);
                                  clearErrors();
                                }}
                                style={{ width: 120 }}
                                className={`btn fs-12 btn${encType !== 'traces' ? '-ghost' : ''}-purple`}
                              >
                                {t('MapModals.AddEnc.step1.signs')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {encType === 'pest' && (
                      <>
                        <div className="row mb-2">
                          <div className="col-xl-12">
                            <Controller
                              control={control}
                              name="pest"
                              rules={{ required: t('MapModals.AddVul.required') }}
                              render={({ field, fieldState: { error } }) => (
                                <MLSelect
                                  required
                                  {...field}
                                  error={error?.message}
                                  label={t('MapModals.AddEnc.step1.selectPest')}
                                  value={field.value}
                                  onChange={field.onChange}
                                  options={pestOptions}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-xl-12">
                            <Controller
                              control={control}
                              name="amount"
                              render={({ field, fieldState: { error } }) => (
                                <MLInput
                                  {...field}
                                  type="number"
                                  value={field.value ?? ''}
                                  onChange={e => {
                                    const val = e.target.value;
                                    field.onChange(val === '' ? null : Number(val));
                                  }}
                                  error={error?.message}
                                  label={t('MapModals.AddEnc.step1.number')}
                                  onBlur={() => trigger('amount')}
                                  required
                                />
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {encType === 'traces' && (
                      <div className="row mb-2">
                        <div className="col-xl-12">
                          <Controller
                            control={control}
                            name="pest_traces"
                            rules={{ required: t('MapModals.AddVul.required') }}
                            render={({ field, fieldState: { error } }) => (
                              <MLSelect
                                required
                                {...field}
                                error={error?.message}
                                label={t('MapModals.AddEnc.step1.selectSigns')}
                                value={field.value}
                                onChange={field.onChange}
                                options={tracesOptions}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}

                    <div className="row mb-2">
                      <div className="col-xl-12">
                        <Controller
                          control={control}
                          name="place"
                          render={({ field, fieldState: { error } }) => (
                            <MLInput
                              {...field}
                              error={error?.message}
                              label={t('MapModals.AddEnc.step1.location')}
                              onBlur={() => trigger('place')}
                              required
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="alert alert-success">{t('MapModals.AddEnc.step1.enoughData')}</div>
                  </div>
                )}
                {step === 2 && (
                  <div>
                    <div className="row mb-2">
                      <div className="col-xl-12">
                        <Controller
                          control={control}
                          name="witness"
                          rules={{ required: t('MapModals.AddVul.required') }}
                          render={({ field, fieldState: { error } }) => (
                            <MLSelect
                              required
                              {...field}
                              error={error?.message}
                              label={t('MapModals.AddEnc.step2.selectWitness')}
                              value={field.value}
                              onChange={field.onChange}
                              options={witnessOptions}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-xl-12">
                        <Controller
                          control={control}
                          name="responsible_person"
                          rules={{ required: t('MapModals.AddVul.required') }}
                          render={({ field, fieldState: { error } }) => (
                            <MLSelect
                              required
                              {...field}
                              error={error?.message}
                              label={t('MapModals.AddEnc.step2.selectResponsible')}
                              value={field.value}
                              onChange={field.onChange}
                              options={responsibleOptions}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-xl-12">
                        <Controller
                          control={control}
                          name="discovery_datetime"
                          rules={{ required: t('MapModals.AddVul.required') }}
                          render={({ field, fieldState: { error } }) => (
                            <MLInput
                              {...field}
                              type="datetime-local"
                              error={error?.message}
                              label={t('MapModals.AddEnc.step2.discoveryTime')}
                              required
                              value={field.value ? field.value.substring(0, 16) : ''}
                              onChange={e => field.onChange(e.target.value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-xl-12">
                        <Controller
                          control={control}
                          name="creation_comment"
                          render={({ field, fieldState: { error } }) => (
                            <MLTextArea
                              {...field}
                              className="form-control"
                              error={error?.message}
                              label={t('MapModals.AddVul.comment')}
                              placeholder={t('MapModals.AddEnc.step2.commentPlaceholder')}
                              rows={3}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {step === 3 && (
                  <div>
                    <div className="row mb-3">
                      <Controller
                        name="photos"
                        control={control}
                        render={({ field }) => (
                          <MLInput
                            label={t('MapModals.AddVul.photo')}
                            type="file"
                            multiple
                            onChange={e => {
                              const files = e.target.files ? Array.from(e.target.files) : [];
                              field.onChange(files.length > 0 ? files : null);
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px 24px 24px',
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            {step > 1 && (
              <MLButton
                classNames="btn-purple"
                onClick={() => setStep(step - 1)}
                innerText={t('MapModals.AddEnc.footer.prev')}
              />
            )}
            {step < 3 && (
              <MLButton
                classNames="btn-purple"
                onClick={handleNextStep}
                innerText={t('MapModals.AddEnc.footer.next')}
              />
            )}
            {step === 1 && (
              <MLButton
                color="success"
                onClick={handleSubmit(async formData => {
                  let payload = { ...formData, responsible_person: null };
                  try {
                    await postEncounterOnMap({ encounter: payload, objectSlug });
                    enqueueSnackbar(t('MapModals.AddEnc.footer.success'), { variant: 'success' });
                    refetchEncounters();
                    onReset();
                  } catch (error) {
                    enqueueSnackbar(t('MapModals.AddEnc.footer.error'), { variant: 'error' });
                  }
                })}
                innerText={t('MapModals.AddEnc.stepper.quickAdd')}
              />
            )}
            {step === 3 && (
              <MLButton
                color="success"
                onClick={handleSubmit(async formData => {
                  formData.is_blitz_creation = false; // Directly update the value
                  try {
                    await postEncounterOnMap({ encounter: formData, objectSlug });
                    enqueueSnackbar(t('MapModals.AddEnc.footer.success'), { variant: 'success' });
                    refetchEncounters();
                    onReset();
                  } catch (error) {
                    enqueueSnackbar(t('MapModals.AddEnc.footer.error'), { variant: 'error' });
                  }
                })}
                innerText={t('MapModals.AddEnc.footer.create')}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEncounterOnMapModal;
